import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthenticatedUser, AuthTokens, LoginRequest } from '@/lib/shared-types';
import { api, tokenStore } from '@/lib/api';
import { disconnectSocket } from '@/lib/realtime';
import { clearCatalogCache } from './catalogCache';

interface AuthState {
  user: AuthenticatedUser | null;
  status: 'idle' | 'loading' | 'authenticated' | 'error';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  status: 'idle',
  error: null,
};

export const login = createAsyncThunk<AuthenticatedUser, LoginRequest>(
  'auth/login',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post<AuthTokens & { user: AuthenticatedUser }>(
        '/auth/login',
        payload,
      );
      tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      return data.user;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message ?? 'Login failed');
    }
  },
);

export const fetchMe = createAsyncThunk<AuthenticatedUser | null>('auth/me', async () => {
  if (!tokenStore.get()) return null;
  const { data } = await api.get<AuthenticatedUser>('/auth/me');
  return data;
});

export const logout = createAsyncThunk<void>('auth/logout', async () => {
  try {
    await api.post('/auth/logout');
  } finally {
    tokenStore.set(null);
    /**
     * The socket is part of the session, and it was the part nobody ended.
     *
     * A socket is authenticated **once, at connect**, and this app keeps one for
     * the whole tab. Sign-out clears the tokens and routes to `/login`
     * client-side with no reload, so without this the socket stayed open,
     * connected as the person who just left, still holding their rooms — and
     * `getSocket()` handed that same object to whoever signed in next, whose
     * room joins were then authorized against the previous user's scopes.
     */
    disconnectSocket();
    /**
     * The menu on disk is the other thing that outlived the session. It is
     * kept for seven days so a till can trade offline, and on a shared terminal
     * that meant the next operator's first render came out of the last one's
     * catalog. Best-effort: a browser that refuses IndexedDB must not be able
     * to hold anyone signed in.
     */
    await clearCatalogCache().catch(() => {});
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthenticatedUser>) => {
        state.status = 'authenticated';
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'error';
        state.error = String(action.payload ?? 'Login failed');
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = action.payload ? 'authenticated' : 'idle';
      })
      .addCase(fetchMe.rejected, (state) => {
        state.status = 'idle';
        state.user = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.status = 'idle';
      });
  },
});

export default authSlice.reducer;
